import Guard from 'arg-guard'

import Logger from '@/logger'
import JWT from '@/models/jwt'

/**
 * Base structure of a context.
 * @export Context
 * @class Context
 */
export default class Context {

    /**
     * Creates an instance of a Context.
     * @param {string} type The type of the context.
     * @param {?string} token An optional authentication token.
     * @memberof Context
     */
    constructor(type, token) {
        Guard.throwIf(type, 'type')

        this._type = type
        this._token = token || null
    }

    /**
     * Gets the type of the context.
     * @readonly
     * @memberof Context
     */
    get type() {
        return this._type
    }

    /**
     * Gets the token for the context.
     * @type {JWT}
     * @readonly
     * @memberof Context
     */
    get token() {
        return new JWT(this._token)
    }

    /**
     * Gets the token for the context.
     * @type {string}
     * @readonly
     * @memberof Context
     */
    get rawToken() {
        return this._token
    }

    /**
     * Returns the object in a form suitable to store.
     * @memberof Context
     */
    toStoreJson() {
        // noinspection JSUnresolvedReference - id is set by ineriting classes.
        return {
            type: this.type,
            id: this.id ? this.id : null,
            token: this._token ? this._token : 'unused',
        }
    }

    /**
     * Returns the object type from raw stored JSON data.
     * @param {?Object} data The stored data.
     * @returns {Context|never}
     * @throws {TypeError} The provided context type must implement its own fromStoreJson() method to return the correct object data.
     * @memberof Context
     */
    static fromStoreJson(data = null) {
        Logger.logObject(data, 'Attempted to create an invalid context.', 'Context')
        throw new TypeError(`The provided context type must implement it's own fromStoreJson() method to return the correct object data.`)
    }
}
